// The specific reasons this module's backend gives, in the four languages.
//
// Two codes travel INSIDE an error and are not the code it is thrown with:
//
//   · `reasons` — what a generic refusal carries in `details.reason` or
//     `details.code` (`ConflictError(…, 'CONFLICT', { reason: 'ALREADY_CLOSED' })`).
//     errorMessage() says it from `errors.reasons.<REASON>` instead of the
//     generic «that record is no longer in the state you expected».
//   · `fields` — the code of each field of a ValidationError, said by
//     fieldErrors() from `errors.fields.<CODE>` instead of «not a valid value».
//
// Generic field codes (EMPTY, IN_THE_FUTURE, NOT_FOUND…) are the shell's and
// are not repeated here. platform/web/tests/errorReasons.test.js reads the
// backend and fails on a code without its four sentences, and on two
// modules saying one code differently (the shell merges them into one).

const errorReasons = {
  es: {
    reasons: {
      NUMBER_TAKEN: 'Ese número de factura ya existe',
      NUMBER_IN_SERIES: 'Ese número es de una serie que numera el propio ERP',
      ORIGIN_ALREADY_IMPORTED: 'Esa factura del otro sistema ya se importó',
    },
    fields: {
      RECTIFIES_NOT_ISSUED: 'Solo se rectifica una factura emitida',
      RECTIFICATIVE_CUSTOMER: 'Una rectificativa va al mismo cliente y NIF que la factura que corrige',
      OVER_RECTIFIED: 'Rectifica más de lo que queda de la factura original',
      NEGATIVE_TOTAL: 'Una factura ordinaria no puede salir en negativo: un abono es una rectificativa',
      BAD_INVOICE_TYPE: 'Ese tipo de factura no se puede registrar',
      BAD_TOTAL: 'El total no cuadra',
      FUTURE_ISSUE_DATE: 'La fecha de emisión no puede ser futura',
      INVALID_AMOUNT: 'El importe no es válido',
      ISSUE_DATE_BEFORE_PREVIOUS: 'La fecha es anterior a la de la última factura de la serie',
      NOTHING_TO_BILL: 'Esa orden no tiene horas ni materiales que facturar',
      NO_CUSTOMER: 'Falta el cliente',
      NO_ISSUER_TAX_ID: 'Falta el NIF de la empresa en sus datos',
      NO_ISSUE_DATE: 'Falta la fecha de emisión',
      NO_LINES: 'La factura no tiene líneas',
      NO_NUMBER: 'La factura no tiene número',
      OVER_OUTSTANDING: 'Es más de lo que queda por cobrar',
      TOTALS_MISMATCH: 'Los totales no cuadran con las líneas',
    },
  },
  ca: {
    reasons: {
      NUMBER_TAKEN: 'Aquest número de factura ja existeix',
      NUMBER_IN_SERIES: 'Aquest número és d’una sèrie que numera el mateix ERP',
      ORIGIN_ALREADY_IMPORTED: 'Aquesta factura de l’altre sistema ja es va importar',
    },
    fields: {
      RECTIFIES_NOT_ISSUED: 'Només es rectifica una factura emesa',
      RECTIFICATIVE_CUSTOMER: 'Una rectificativa va al mateix client i NIF que la factura que corregeix',
      OVER_RECTIFIED: 'Rectifica més del que queda de la factura original',
      NEGATIVE_TOTAL: 'Una factura ordinària no pot sortir en negatiu: un abonament és una rectificativa',
      BAD_INVOICE_TYPE: 'Aquest tipus de factura no es pot registrar',
      BAD_TOTAL: 'El total no quadra',
      FUTURE_ISSUE_DATE: 'La data d’emissió no pot ser futura',
      INVALID_AMOUNT: 'L’import no és vàlid',
      ISSUE_DATE_BEFORE_PREVIOUS: 'La data és anterior a la de l’última factura de la sèrie',
      NOTHING_TO_BILL: 'Aquesta ordre no té hores ni materials per facturar',
      NO_CUSTOMER: 'Falta el client',
      NO_ISSUER_TAX_ID: 'Falta el NIF de l’empresa a les seves dades',
      NO_ISSUE_DATE: 'Falta la data d’emissió',
      NO_LINES: 'La factura no té línies',
      NO_NUMBER: 'La factura no té número',
      OVER_OUTSTANDING: 'És més del que queda per cobrar',
      TOTALS_MISMATCH: 'Els totals no quadren amb les línies',
    },
  },
  en: {
    reasons: {
      NUMBER_TAKEN: 'That invoice number already exists',
      NUMBER_IN_SERIES: 'That number belongs to a series the ERP numbers itself',
      ORIGIN_ALREADY_IMPORTED: 'That invoice from the other system was already imported',
    },
    fields: {
      RECTIFIES_NOT_ISSUED: 'Only an issued invoice can be rectified',
      RECTIFICATIVE_CUSTOMER: 'A rectificative invoice goes to the same customer and tax id as the one it corrects',
      OVER_RECTIFIED: 'That rectifies more than is left of the original invoice',
      NEGATIVE_TOTAL: 'An ordinary invoice cannot total less than zero: a credit is a rectificative invoice',
      BAD_INVOICE_TYPE: 'That type of invoice cannot be filed',
      BAD_TOTAL: 'The total does not add up',
      FUTURE_ISSUE_DATE: 'The issue date cannot be in the future',
      INVALID_AMOUNT: 'That amount is not valid',
      ISSUE_DATE_BEFORE_PREVIOUS: 'The date is earlier than the last invoice of the series',
      NOTHING_TO_BILL: 'That order has no hours or materials to invoice',
      NO_CUSTOMER: 'The customer is missing',
      NO_ISSUER_TAX_ID: 'The company’s tax id is missing from its details',
      NO_ISSUE_DATE: 'The issue date is missing',
      NO_LINES: 'The invoice has no lines',
      NO_NUMBER: 'The invoice has no number',
      OVER_OUTSTANDING: 'That is more than is still owed',
      TOTALS_MISMATCH: 'The totals do not match the lines',
    },
  },
  fr: {
    reasons: {
      NUMBER_TAKEN: 'Ce numéro de facture existe déjà',
      NUMBER_IN_SERIES: 'Ce numéro appartient à une série que l’ERP numérote lui-même',
      ORIGIN_ALREADY_IMPORTED: 'Cette facture de l’autre système a déjà été importée',
    },
    fields: {
      RECTIFIES_NOT_ISSUED: 'Seule une facture émise peut être rectifiée',
      RECTIFICATIVE_CUSTOMER: 'Une facture rectificative va au même client et au même numéro fiscal que celle qu\'elle corrige',
      OVER_RECTIFIED: 'Cela rectifie plus que ce qui reste de la facture originale',
      NEGATIVE_TOTAL: 'Une facture ordinaire ne peut pas être négative : un avoir est une facture rectificative',
      BAD_INVOICE_TYPE: 'Ce type de facture ne peut pas être enregistré',
      BAD_TOTAL: 'Le total ne correspond pas',
      FUTURE_ISSUE_DATE: 'La date d’émission ne peut pas être dans le futur',
      INVALID_AMOUNT: 'Ce montant n’est pas valide',
      ISSUE_DATE_BEFORE_PREVIOUS: 'La date est antérieure à celle de la dernière facture de la série',
      NOTHING_TO_BILL: 'Cet ordre n’a ni heures ni matériel à facturer',
      NO_CUSTOMER: 'Le client manque',
      NO_ISSUER_TAX_ID: 'Le numéro fiscal de l’entreprise manque dans ses informations',
      NO_ISSUE_DATE: 'La date d’émission manque',
      NO_LINES: 'La facture n’a pas de lignes',
      NO_NUMBER: 'La facture n’a pas de numéro',
      OVER_OUTSTANDING: 'C’est plus que ce qui reste dû',
      TOTALS_MISMATCH: 'Les totaux ne correspondent pas aux lignes',
    },
  },
}

/** The module's catalogue with these hung from `errors`, locale by locale. */
export function withErrorReasons(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = errorReasons[locale] ?? {}
    merged[locale] = {
      ...catalogue,
      errors: {
        ...catalogue.errors,
        reasons: { ...catalogue.errors?.reasons, ...own.reasons },
        fields: { ...catalogue.errors?.fields, ...own.fields },
      },
    }
  }
  return merged
}

export default errorReasons
